import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

interface AutoScrollToCommentContextType {
  urlCommentId?: string
  hasScrolledToComment: boolean
  handleScrollToComment(): void
}

export const AutoScrollToCommentContext = createContext<AutoScrollToCommentContextType>({
  urlCommentId: undefined,
  hasScrolledToComment: false,
  handleScrollToComment: () => undefined,
})

export function useCommentToScrollTo(commentId: string) {
  const { urlCommentId, hasScrolledToComment, handleScrollToComment } = useContext(AutoScrollToCommentContext)

  const isUrlComment = commentId === urlCommentId
  const shouldScrollToComment = isUrlComment && !hasScrolledToComment

  const commentElementRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (shouldScrollToComment && commentElementRef.current) {
      // Add a delay to ensure component is mounted and layout is complete
      // before attempting to scroll to the comment
      const timeoutId = setTimeout(() => {
        // First scroll with instant behavior to ensure position
        // This happens immediately
        commentElementRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' })

        // Then apply smooth scroll
        // Second scroll waits for the next frame
        requestAnimationFrame(() => {
          commentElementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          handleScrollToComment()
        })
      }, 300)

      return () => clearTimeout(timeoutId)
    }
  }, [shouldScrollToComment, handleScrollToComment])

  return { commentElementRef, isUrlComment }
}

// Hook to listen to changes in the commentId in the URL
// Used by things like the comment widget, that needs to automatically open to render the comment
export function useListenToUrlCommentChange(handleUrlCommentChange: (commentId: string) => void) {
  const { urlCommentId } = useContext(AutoScrollToCommentContext)

  const callbackRef = useRef(handleUrlCommentChange)
  callbackRef.current = handleUrlCommentChange

  useEffect(() => {
    const commentIdFromUrl = getCommentIdFromUrlHash()
    if (!urlCommentId || commentIdFromUrl !== urlCommentId) {
      return
    }
    callbackRef.current(urlCommentId)
  }, [urlCommentId])
}

export function useUrlComment() {
  return useContext(AutoScrollToCommentContext).urlCommentId
}

export function getCommentIdFromUrlHash(): string | undefined {
  if (typeof window === 'undefined') return undefined

  const { hash } = window.location

  if (hash.startsWith('#comment-')) {
    return hash.replace('#comment-', '') || undefined
  }

  return undefined
}

export const AutoScrollToCommentProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [urlCommentId, setUrlCommentId] = useState(getCommentIdFromUrlHash)
  const [hasScrolledToComment, setHasScrolledToComment] = useState(false)

  const handleScrollToComment = useCallback(() => {
    setHasScrolledToComment(true)
  }, [])

  // listen for URL changes
  useEffect(() => {
    const handleHashChange = () => {
      const newUrlComment = getCommentIdFromUrlHash()
      setUrlCommentId(newUrlComment)
      setHasScrolledToComment(false)
    }

    window.addEventListener('hashchange', handleHashChange)

    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  return (
    <AutoScrollToCommentContext.Provider
      value={{
        urlCommentId,
        hasScrolledToComment,
        handleScrollToComment,
      }}
    >
      {children}
    </AutoScrollToCommentContext.Provider>
  )
}
